//@flow
import * as Immutable from 'immutable';

import {
    Plan,
    VehicleGroup,
    PlanConfig,
    Progress,
    VehicleTour,
    AppFieldItem,
    AppFields,
    PlanSummary,
    UnassignedTask,
    Vehicle,
    TourVisit,
    UserTourSummary,
    TourId,
    UserVehicle,
    AssignedUser,
    ResourceUtilization,
    VehicleId,
    VehicleModel,
    OperationShift,
    VisitLocation,
    TimeDistancePair,
    VisitVolume,
    ConstraintViolation,
    OrderDetail,
    UserAlias,
    VehicleModelId,
    MinimalLocation,
    Break,
    VisitSlot,
    Resources,
    PlanEditsSummary,
    Metric,
    CustomFieldMetric,
    AdditionalValue,
    UsedRoutes,
    VehicleCount,
    CarbonEmission
} from 'common/models/flow/Plan';

import type {
    PlanModelType,
    VehicleGroupModelType,
    PlanConfigModelType,
    ProgressModelType,
    VehicleTourModelType,
    AppFieldItemModelType,
    AppFieldsModelType,
    PlanSummaryModelType,
    UnassignedTaskModelType,
    VehicleModelType,
    TourVisitModelType,
    UserTourSummaryModelType,
    TourIdModelType,
    AssignedUserModelType,
    ResourceUtilizationModelType,
    VehicleIdModelType,
    VehicleModelModelType,
    OperationShiftModelType,
    VisitLocationModelType,
    TimeDistancePairModelType,
    VisitVolumeModelType,
    ConstraintViolationModelType,
    OrderDetailModelType,
    UserAliasModelType,
    VehicleModelIdModelType,
    MinimalLocationModelType,
    BreakModelType,
    VisitSlotModelType,
    ResourcesModelType,
    UserVehicleModelType,
    PlanEditsSummaryModelType,
    MetricModelType,
    CustomFieldMetricModelType,
    AdditionalValueModelType,
    VisitSourceEnum as VisitSourceEnumType,
    UsedRoutesModelType,
    VehicleCountModelType,
    CarbonEmissionModelType
} from 'common/models/flow/Plan';
import type { TourDayModelType } from 'common/models/flow/TourDay';

import { Geometry } from './Geometry';
import { StructuredAddress } from './StructuredAddress';
import { LineItem } from './LineItem';

import { Skills } from './Skills';

import { RouteRestriction } from './RouteRestriction';
import { ResourceDefinition } from './ResourceDefinition';

import { TimeSlot } from './TimeSlot';

import { AmountTransaction } from './AmountTransaction';

import { ContactPoint } from './ContactPoint';
import { VehicleCostModel } from './VehicleCostModel';
import { TourFilter } from './TourFilter';

import { VisitResource } from './VisitResource';
import { Amount } from './Amount';

import I18nHelper from 'common/helpers/I18nHelper';

import { ShiftInstant } from './ShiftInstant';

import { ResourceLimit } from './ResourceLimit';

import { VolumeLimit } from './VolumeLimit';

import { VersionedMapId } from './VersionedMapId';
import { TourDay } from './TourDay';
import { UnassignedDetail } from './UnassignedDetail';
import { TourDayReason } from './TourDayReason';
import TimeUtils from 'common/utils/TimeUtils';
import {
    UserVisitTypeEnum,
    BreakTypeEnum,
    VisitStatusEnum,
    VisitSourceEnum
} from 'common/enums/VisitEnums';
import { TaskStatusUpdate } from 'common/models/internal/StatusUpdate';
import { MasterLocation } from 'common/models/internal/MasterLocation';
import { LocationId } from 'common/models/internal/LocationId';

//Validation stuff
import {
    validatable,
    validate,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';

import { GeocodingMetadata } from './GeocodingMetadata';
import { CrossDockVehicleConfig } from 'common/models/flow/CrossDockVehicleConfig';

import { ZoneMapGeometry } from './ZoneMapGeometry';
import { Team } from './Team';
import { TriggeredGeofence } from './TriggeredGeofence';
import { PlanStatusEnum } from 'common/enums/PlanEnums';

const translationsNamespace = 'validations';
const plannerTranslationsNamespace = ['planner'];
const plannerNsTranslate = I18nHelper.getTranslate(
    plannerTranslationsNamespace
);

class InternalAdditionalValue extends AdditionalValue {
    static fromJS(json: AdditionalValueModelType): InternalAdditionalValue {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalUsedRoutes extends UsedRoutes {
    static fromJS(json: UsedRoutesModelType): InternalUsedRoutes {
        const state: Object = Object.assign({}, json);
        state.mapId = VersionedMapId.fromJS(state.mapId);
        return new this(Immutable.Map(state));
    }

    get mapId(): VersionedMapId {
        return this._state.get('mapId');
    }
}

class InternalVehicleCount extends VehicleCount {
    static fromJS(json: VehicleCountModelType): InternalVehicleCount {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalCarbonEmission extends CarbonEmission {
    static fromJS(json: CarbonEmissionModelType): InternalCarbonEmission {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

export {
    InternalAdditionalValue as AdditionalValue,
    InternalUsedRoutes as UsedRoutes,
    InternalVehicleCount as VehicleCount,
    InternalCarbonEmission as CarbonEmission
};

export const WeekType = {
    Week1: 'WEEK1',
    Week2: 'WEEK2',
    Week3: 'WEEK3',
    Week4: 'WEEK4'
};
class InternalPlan extends Plan {
    static fromJS(json: PlanModelType): InternalPlan {
        const state: Object = Object.assign({}, json);
        state.vehicleGroups = Immutable.List(state.vehicleGroups).map(item =>
            InternalVehicleGroup.fromJS(item)
        );
        state.configuration = InternalPlanConfig.fromJS(state.configuration);
        state.replanIds = Immutable.List(state.replanIds);
        state.progress = Immutable.List(state.progress).map(item =>
            InternalProgress.fromJS(item)
        );
        state.tours = Immutable.List(state.tours).map(item =>
            InternalVehicleTour.fromJS(item)
        );
        state.summary = InternalPlanSummary.fromJS(state.summary);
        state.unassignedDetails = Immutable.List(
            state.unassignedDetails
        ).map(item => UnassignedDetail.fromJS(item));
        state.unassignedTasks = Immutable.List(
            state.unassignedTasks
        ).map(item => InternalUnassignedTask.fromJS(item));
        state.routeRestrictions = Immutable.List(
            state.routeRestrictions
        ).map(item => RouteRestriction.fromJS(item));
        state.tourFilters = Immutable.List(state.tourFilters).map(item =>
            TourFilter.fromJS(item)
        );
        state.mapId = state.mapId
            ? VersionedMapId.fromJS(state.mapId)
            : state.mapId;
        return new this(Immutable.Map(state));
    }

    getMapId() {
        return this.mapId?.mapId?.mapId;
    }

    getMapIdVersion() {
        return this.mapId?.version;
    }

    hasPredictedPolyline(): boolean {
        return this.tours.some(tour => tour.hasPredictedPolyline());
    }

    isPlanInProgress() {
        if (
            this.status === 'PLANNED' ||
            this.status === 'ASSIGNED' ||
            this.status === 'ASSIGNING' ||
            this.status === 'FROZEN'
        ) {
            return false;
        }
        return true;
    }

    isEdited() {
        const { summary } = this;
        if (summary && summary.editsCount > 0) {
            return true;
        }
        return false;
    }

    findTourIndex(tourId: string): number {
        return this.tours.findIndex(tour => tour.tourId.tourId === tourId);
    }

    hasSnapshots = () => {
        return this.replanIds.size > 0;
    };

    // @TODO: Need to remove, once tripCount start coming from BE in tour summary
    getTotalTripsCount() {
        let totalTrips = 0;
        const { tours } = this;

        tours.forEach(tour => {
            if (tour.hasTasks()) {
                totalTrips += 1;
            }
        });
        return totalTrips;
    }

    getProgress() {
        var { progress } = this;
        var progressTitle;
        var progressSteps = progress.size;

        if (this.status === PlanStatusEnum.Terminated) {
            return {
                info: I18nHelper.translate(
                    translationsNamespace,
                    'planGenerationTerminated',
                    { id: this.planId }
                ),
                value: 0
            };
        }

        if (this.isPlanInProgress()) {
            if (this.status === 'RECEIVED') {
                return {
                    info: I18nHelper.translate(
                        translationsNamespace,
                        'submitted'
                    ),
                    value: 0
                };
            }
            for (var i = 0; i < progressSteps; i++) {
                if (progress.get(i).value < 100) {
                    progressTitle = I18nHelper.translate(
                        translationsNamespace,
                        'planGenerationProgress',
                        {
                            id: this.planId,
                            current: i + 1,
                            total: progressSteps
                        }
                    );
                    return {
                        info: progressTitle,
                        value:
                            Math.round(
                                ((i * 100 + progress.get(i).value) /
                                    progressSteps) *
                                    100
                            ) / 100
                    };
                }
            }
        }
        return {
            info: I18nHelper.translate(
                translationsNamespace,
                'allStepsCompleted'
            ),
            value: 100
        };
    }

    shouldMergeVisitsForTours() {
        return this.isMinimalPlan && this.metadataOnMinimalPlan;
    }

    get configuration(): InternalPlanConfig {
        return this._state.get('configuration');
    }

    get summary(): InternalPlanSummary {
        return this._state.get('summary');
    }

    get vehicleGroups(): Immutable.List<VehicleGroup> {
        return this._state.get('vehicleGroups');
    }

    get progress(): Immutable.List<Progress> {
        return this._state.get('progress');
    }

    get tours(): Immutable.List<VehicleTour> {
        return this._state.get('tours');
    }

    setTours(tours: Immutable.List<InternalVehicleTour>) {
        return this.clone(this._state.set('tours', tours));
    }

    get unassignedTasks(): Immutable.List<UnassignedTask> {
        return this._state.get('unassignedTasks');
    }

    get routeRestrictions(): Immutable.List<RouteRestriction> {
        return this._state.get('routeRestrictions');
    }

    get tourFilters(): Immutable.List<TourFilter> {
        return this._state.get('tourFilters');
    }

    get mapId(): ?VersionedMapId {
        return this._state.get('mapId');
    }

    get unassignedDetails(): Immutable.List<UnassignedDetail> {
        return this._state.get('unassignedDetails');
    }
}

class InternalVehicleGroup extends VehicleGroup {
    static fromJS(json: VehicleGroupModelType): InternalVehicleGroup {
        const state: Object = Object.assign({}, json);
        state.vehicles = Immutable.List(state.vehicles).map(item =>
            InternalVehicle.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    get vehicles(): Immutable.List<Vehicle> {
        return this._state.get('vehicles');
    }
}

class InternalPlanConfig extends PlanConfig {
    static fromJS(json: PlanConfigModelType): InternalPlanConfig {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalProgress extends Progress {
    static fromJS(json: ProgressModelType): InternalProgress {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalVehicleTour extends VehicleTour {
    static fromJS(json: VehicleTourModelType): InternalVehicleTour {
        const state: Object = Object.assign({}, json);
        state.visits = Immutable.List(state.visits).map(item =>
            InternalTourVisit.fromJS(item)
        );
        state.tourDaysInfo = Immutable.List(state.tourDaysInfo).map(item =>
            TourDayReason.fromJS(item)
        );
        state.summary = InternalUserTourSummary.fromJS(state.summary);
        state.tourId = InternalTourId.fromJS(state.tourId);
        state.sourceTourId = InternalTourId.fromJS(state.sourceTourId);
        state.tourDay = state.tourDay
            ? TourDay.fromJS(state.tourDay)
            : state.tourDay;
        state.tags = Immutable.List(state.tags);
        state.vehicle = InternalUserVehicle.fromJS(state.vehicle);
        state.assignedUser = state.assignedUser
            ? InternalAssignedUser.fromJS(state.assignedUser)
            : state.assignedUser;
        state.customProperties = Immutable.Map(state.customProperties);
        return new this(Immutable.Map(state));
    }

    hasPredictedPolyline(): boolean {
        return this.visits.some(visit => !!visit.predictedPolyline);
    }

    hasSourceOrderId(): boolean {
        return this.visits.some(visit => !!visit.sourceOrderId);
    }

    hasTasks(): boolean {
        return this.summary.taskCount > 0;
    }

    getSourceTourId = (): string => {
        // handled backward compatibility, in older plans sourceTourId is null
        return this.sourceTourId?.tourId || this.tourId.tourId;
    };

    isOverlappingWithTour(otherTour: VehicleTour): boolean {
        const {
            summary: { startTime, endTime }
        } = this;
        const {
            summary: {
                startTime: otherTourStartTime,
                endTime: otherTourEndTime
            }
        } = otherTour;

        const startDate = new Date(startTime);
        const endDate = new Date(endTime);
        const otherTourStartDate = new Date(otherTourStartTime);
        const otherTourEndDate = new Date(otherTourEndTime);

        return TimeUtils.isOverlappingDateRanges(
            [startDate, endDate],
            [otherTourStartDate, otherTourEndDate]
        );
    }

    getResourceUtilizationValue(resource): number {
        const { used } = resource;
        return used;
    }

    get summary(): InternalUserTourSummary {
        return this._state.get('summary');
    }

    get tourId(): InternalTourId {
        return this._state.get('tourId');
    }

    get vehicle(): InternalUserVehicle {
        return this._state.get('vehicle');
    }

    get tourDay(): ?TourDay {
        return this._state.get('tourDay');
    }

    get assignedUser(): ?InternalAssignedUser {
        return this._state.get('assignedUser');
    }

    get visits(): Immutable.List<TourVisit> {
        return this._state.get('visits');
    }

    setVisits(visits: Immutable.List<InternalTourVisit>): this {
        return this.clone(this._state.set('visits', visits));
    }

    get tourDaysInfo(): Immutable.List<TourDayReason> {
        return this._state.get('tourDaysInfo');
    }

    get sourceTourId(): InternalTourId {
        return this._state.get('sourceTourId');
    }

    getMetaData(): {
        tourDay: ?TourDayModelType,
        tourName: ?string,
        customProperties: ?{ [string]: string },
        tags: ?Array<string>
    } {
        return {
            tourDay: this.tourDay?.toJS(),
            tourName: this.tourName,
            customProperties: this.customProperties.toJS(),
            tags: this.tags.toJS()
        };
    }
}

class InternalAppFieldItem extends AppFieldItem {
    static fromJS(json: AppFieldItemModelType): InternalAppFieldItem {
        const state: Object = Object.assign({}, json);
        state.additionalValues = state.additionalValues
            ? InternalAdditionalValue.fromJS(state.additionalValues)
            : state.additionalValues;
        return new this(Immutable.Map(state));
    }

    get additionalValues(): ?InternalAdditionalValue {
        return this._state.get('additionalValues');
    }
}

class InternalAppFields extends AppFields {
    static fromJS(json: AppFieldsModelType): InternalAppFields {
        const state: Object = Object.assign({}, json);
        state.items = Immutable.List(state.items).map(item =>
            InternalAppFieldItem.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    get items(): Immutable.List<AppFieldItem> {
        return this._state.get('items');
    }
}

class InternalPlanSummary extends PlanSummary {
    static fromJS(json: PlanSummaryModelType): InternalPlanSummary {
        const state: Object = Object.assign({}, json);
        state.vehiclesOperationSlot = TimeSlot.fromJS(
            state.vehiclesOperationSlot
        );
        state.editsSummary = state.editsSummary
            ? InternalPlanEditsSummary.fromJS(state.editsSummary)
            : state.editsSummary;
        state.volume = InternalResourceUtilization.fromJS(state.volume);
        state.time = InternalResourceUtilization.fromJS(state.time);
        state.resources = InternalResources.fromJS(state.resources);
        state.customMetrics = Immutable.Map(state.customMetrics);
        state.totalFreightCost = state.totalFreightCost
            ? Amount.fromJS(state.totalFreightCost)
            : state.totalFreightCost;
        state.vehicleModelsCount = Immutable.Map(
            state.vehicleModelsCount
        ).map(item => InternalVehicleCount.fromJS(item));
        state.vehicleModelsUsed = Immutable.Map(
            state.vehicleModelsUsed
        ).map(item => InternalVehicleCount.fromJS(item));
        state.carbonEmissionMetrics = state.carbonEmissionMetrics
            ? InternalCarbonEmission.fromJS(state.carbonEmissionMetrics)
            : state.carbonEmissionMetrics;
        state.violationTypes = Immutable.List(state.violationTypes);
        return new this(Immutable.Map(state));
    }

    get vehiclesOperationSlot(): TimeSlot {
        return this._state.get('vehiclesOperationSlot');
    }

    get volume(): InternalResourceUtilization {
        return this._state.get('volume');
    }

    get resources(): InternalResources {
        return this._state.get('resources');
    }

    get editsSummary(): ?InternalPlanEditsSummary {
        return this._state.get('editsSummary');
    }

    get totalFreightCost(): ?Amount {
        return this._state.get('totalFreightCost');
    }

    get time(): InternalResourceUtilization {
        return this._state.get('time');
    }

    get carbonEmissionMetrics(): ?InternalCarbonEmission {
        return this._state.get('carbonEmissionMetrics');
    }
}

class InternalUnassignedTask extends UnassignedTask {
    static fromJS(json: UnassignedTaskModelType): InternalUnassignedTask {
        const state: Object = Object.assign({}, json);
        state.visits = Immutable.List(state.visits).map(item =>
            InternalTourVisit.fromJS(item)
        );
        state.skills = Skills.fromJS(state.skills);
        state.possibleDays = Immutable.List(state.possibleDays).map(item =>
            TourDay.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    get skills(): Skills {
        return this._state.get('skills');
    }

    get visits(): Immutable.List<TourVisit> {
        return this._state.get('visits');
    }

    get possibleDays(): Immutable.List<TourDay> {
        return this._state.get('possibleDays');
    }
}

class InternalVehicle extends Vehicle {
    static fromJS(json: VehicleModelType): InternalVehicle {
        const state: Object = Object.assign({}, json);
        state.id = InternalVehicleId.fromJS(state.id);
        state.vehicleModel = InternalVehicleModel.fromJS(state.vehicleModel);
        state.operationShift = InternalOperationShift.fromJS(
            state.operationShift
        );
        state.resourceLimits = Immutable.List(state.resourceLimits).map(item =>
            ResourceLimit.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    get id(): InternalVehicleId {
        return this._state.get('id');
    }

    get vehicleModel(): InternalVehicleModel {
        return this._state.get('vehicleModel');
    }

    get operationShift(): InternalOperationShift {
        return this._state.get('operationShift');
    }

    get resourceLimits(): Immutable.List<ResourceLimit> {
        return this._state.get('resourceLimits');
    }
}

class InternalTourVisit extends TourVisit {
    static fromJS(json: TourVisitModelType): InternalTourVisit {
        const state: Object = Object.assign({}, json);
        state.chosenLocation = InternalVisitLocation.fromJS(
            state.chosenLocation
        );
        state.predictedTravel = InternalTimeDistancePair.fromJS(
            state.predictedTravel
        );
        state.volume = InternalVisitVolume.fromJS(state.volume);
        state.resources = Immutable.List(state.resources).map(item =>
            VisitResource.fromJS(item)
        );
        state.violations = Immutable.List(state.violations).map(item =>
            InternalConstraintViolation.fromJS(item)
        );
        state.orderDetail = InternalOrderDetail.fromJS(state.orderDetail);
        state.amountTransaction = AmountTransaction.fromJS(
            state.amountTransaction
        );
        state.appFields = InternalAppFields.fromJS(state.appFields);
        state.skills = Skills.fromJS(state.skills);
        state.possibleDays = Immutable.List(state.possibleDays).map(item =>
            TourDay.fromJS(item)
        );
        state.locationId = state.locationId
            ? LocationId.fromJS(state.locationId)
            : state.locationId;
        state.locationDetails = state.locationDetails
            ? MasterLocation.fromJS(state.locationDetails)
            : state.locationDetails;
        state.statusUpdates = TaskStatusUpdate.fromJS(state.statusUpdates);
        state.triggeredGeofences = state.triggeredGeofences
            ? Immutable.List(state.triggeredGeofences).map(item =>
                  TriggeredGeofence.fromJS(item)
              )
            : state.triggeredGeofences;
        state.routes = Immutable.List(state.routes).map(item =>
            ZoneMapGeometry.fromJS(item)
        );
        state.team = Team.fromJS(state.team);
        return new this(Immutable.Map(state));
    }

    getCombinedId(): string {
        return `${this.clientId}/${this.sourceId}/${this.id}`;
    }

    isHomebase(): boolean {
        return this.visitType === 'HOMEBASE';
    }

    isPickup(): boolean {
        return this.transactionType === 'PICKUP' ? true : false;
    }

    isDrop(): boolean {
        return this.transactionType === 'DROP' ? true : false;
    }

    isUserVisit(): boolean {
        return this.visitSource === 'USER';
    }

    isTaskVisit(): boolean {
        return this.visitSource === 'TASK';
    }

    isTerminal(): boolean {
        return (
            this.visitStatus === VisitStatusEnum.Cancelled ||
            this.visitStatus === VisitStatusEnum.Completed
        );
    }

    getLatLng() {
        return this.chosenLocation.geometry.latLng;
    }

    getAmountTransactionTypeText() {
        const transaction = this.amountTransaction;
        if (!transaction) {
            return 'NONE';
        }
        return transaction.getExchangeTypeText();
    }

    isSameSource(visitSource: VisitSourceEnumType): boolean {
        const UserAndTour = [VisitSourceEnum.USER, 'TOUR'];
        if (
            UserAndTour.includes(this.visitSource) &&
            UserAndTour.includes(visitSource)
        ) {
            //Temp change in backend: User and Tour are interchangable
            return true;
        }
        return this.visitSource === visitSource;
    }

    isSameVisit(visit: InternalTourVisit): boolean {
        return (
            this.isSameSource(visit.visitSource) &&
            this.id === visit.id &&
            (this.sourceId === visit.sourceId ||
                (visit.mergedVisitSourceId || '').includes(this.sourceId))
        );
    }
    isLatestIncrementalPlanVisit: (planId: string) => boolean = planId => {
        return this.allocatedPlanId === planId;
    };

    get chosenLocation(): InternalVisitLocation {
        return this._state.get('chosenLocation');
    }

    get predictedTravel(): InternalTimeDistancePair {
        return this._state.get('predictedTravel');
    }

    get volume(): InternalVisitVolume {
        return this._state.get('volume');
    }

    get orderDetail(): InternalOrderDetail {
        return this._state.get('orderDetail');
    }

    get amountTransaction(): AmountTransaction {
        return this._state.get('amountTransaction');
    }

    get appFields(): InternalAppFields {
        return this._state.get('appFields');
    }

    get resources(): Immutable.List<VisitResource> {
        return this._state.get('resources');
    }

    get skills(): Skills {
        return this._state.get('skills');
    }

    get violations(): Immutable.List<ConstraintViolation> {
        return this._state.get('violations');
    }

    get possibleDays(): Immutable.List<TourDay> {
        return this._state.get('possibleDays');
    }

    get visitStatus(): VisitStatusEnum {
        return this._state.get('visitStatus');
    }

    get visitSource(): VisitSourceEnum {
        return this._state.get('visitSource');
    }

    get userVisitType(): UserVisitTypeEnum {
        return this._state.get('userVisitType');
    }

    get locationId(): ?LocationId {
        return this._state.get('locationId');
    }

    get locationDetails(): ?MasterLocation {
        return this._state.get('locationDetails');
    }

    get statusUpdates(): TaskStatusUpdate {
        return this._state.get('statusUpdates');
    }

    get routes(): Immutable.List<ZoneMapGeometry> {
        return this._state.get('routes');
    }

    get team(): Team {
        return this._state.get('team');
    }

    get triggeredGeofences(): ?Immutable.List<TriggeredGeofence> {
        return this._state.get('triggeredGeofences');
    }
}

class InternalUserTourSummary extends UserTourSummary {
    static fromJS(json: UserTourSummaryModelType): InternalUserTourSummary {
        const state: Object = Object.assign({}, json);
        state.customMetrics = Immutable.Map(state.customMetrics);
        state.volume = InternalResourceUtilization.fromJS(state.volume);
        state.resources = state.resources
            ? InternalResources.fromJS(state.resources)
            : state.resources;
        state.vehiclesOperationSlot = TimeSlot.fromJS(
            state.vehiclesOperationSlot
        );
        state.usedSkills = Skills.fromJS(state.usedSkills);
        state.freightCost = state.freightCost
            ? Amount.fromJS(state.freightCost)
            : state.freightCost;
        state.usedRoutes = state.usedRoutes
            ? Immutable.List(state.usedRoutes).map(item =>
                  InternalUsedRoutes.fromJS(item)
              )
            : state.usedRoutes;
        state.customFieldMetrics = Immutable.List(
            state.customFieldMetrics
        ).map(item => InternalCustomFieldMetric.fromJS(item));
        return new this(Immutable.Map(state));
    }

    get volume(): InternalResourceUtilization {
        return this._state.get('volume');
    }

    get vehiclesOperationSlot(): TimeSlot {
        return this._state.get('vehiclesOperationSlot');
    }

    get resources(): ?InternalResources {
        return this._state.get('resources');
    }

    get usedSkills(): Skills {
        return this._state.get('usedSkills');
    }

    get freightCost(): ?Amount {
        return this._state.get('freightCost');
    }

    get customFieldMetrics(): Immutable.List<CustomFieldMetric> {
        return this._state.get('customFieldMetrics');
    }

    get usedRoutes(): ?Immutable.List<UsedRoutes> {
        return this._state.get('usedRoutes');
    }
}

class InternalTourId extends TourId {
    static fromJS(json: TourIdModelType): InternalTourId {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalUserVehicle extends UserVehicle {
    static fromJS(json: UserVehicleModelType): InternalUserVehicle {
        const state: Object = Object.assign({}, json);
        state.id = InternalVehicleId.fromJS(state.id);
        state.vehicleModel = InternalVehicleModel.fromJS(state.vehicleModel);
        state.operationShift = InternalOperationShift.fromJS(
            state.operationShift
        );
        state.resourceLimits = Immutable.List(state.resourceLimits).map(item =>
            ResourceLimit.fromJS(item)
        );
        state.userAlias = state.userAlias
            ? InternalUserAlias.fromJS(state.userAlias)
            : state.userAlias;
        state.skills = Skills.fromJS(state.skills);
        state.vehicleCostModel = state.vehicleCostModel
            ? VehicleCostModel.fromJS(state.vehicleCostModel)
            : state.vehicleCostModel;
        state.crossDockVehicleConfig = state.crossDockVehicleConfig
            ? CrossDockVehicleConfig.fromJS(state.crossDockVehicleConfig)
            : state.crossDockVehicleConfig;
        return new this(Immutable.Map(state));
    }

    get id(): InternalVehicleId {
        return this._state.get('id');
    }

    get vehicleModel(): InternalVehicleModel {
        return this._state.get('vehicleModel');
    }

    get operationShift(): InternalOperationShift {
        return this._state.get('operationShift');
    }

    get userAlias(): ?InternalUserAlias {
        return this._state.get('userAlias');
    }

    get skills(): Skills {
        return this._state.get('skills');
    }

    get vehicleCostModel(): ?VehicleCostModel {
        return this._state.get('vehicleCostModel');
    }

    get resourceLimits(): Immutable.List<ResourceLimit> {
        return this._state.get('resourceLimits');
    }

    get crossDockVehicleConfig(): ?CrossDockVehicleConfig {
        return this._state.get('crossDockVehicleConfig');
    }
}

class InternalAssignedUser extends AssignedUser {
    static fromJS(json: AssignedUserModelType): InternalAssignedUser {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalResourceUtilization extends ResourceUtilization {
    static fromJS(
        json: ResourceUtilizationModelType
    ): InternalResourceUtilization {
        const state: Object = Object.assign({}, json);
        state.definition = state.definition
            ? ResourceDefinition.fromJS(state.definition)
            : state.definition;
        return new this(Immutable.Map(state));
    }

    get definition(): ?ResourceDefinition {
        return this._state.get('definition');
    }
}

class InternalVehicleId extends VehicleId {
    static fromJS(json: VehicleIdModelType): InternalVehicleId {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalVehicleModel extends VehicleModel {
    static fromJS(json: VehicleModelModelType): InternalVehicleModel {
        const state: Object = Object.assign({}, json);
        state.volumeLimit = VolumeLimit.fromJS(state.volumeLimit);
        state.id = InternalVehicleModelId.fromJS(state.id);
        state.costModel = state.costModel
            ? VehicleCostModel.fromJS(state.costModel)
            : state.costModel;
        state.skills = state.skills
            ? Skills.fromJS(state.skills)
            : state.skills;
        state.resourceLimits = Immutable.List(state.resourceLimits).map(item =>
            ResourceLimit.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    get volumeLimit(): VolumeLimit {
        return this._state.get('volumeLimit');
    }

    get id(): InternalVehicleModelId {
        return this._state.get('id');
    }

    get costModel(): ?VehicleCostModel {
        return this._state.get('costModel');
    }

    get resourceLimits(): Immutable.List<ResourceLimit> {
        return this._state.get('resourceLimits');
    }

    get skills(): ?Skills {
        return this._state.get('skills');
    }
}

@validatable
class InternalOperationShift extends OperationShift {
    static fromJS(json: OperationShiftModelType): InternalOperationShift {
        const state: Object = Object.assign({}, json);
        state.startShiftInstant = ShiftInstant.fromJS(state.startShiftInstant);
        state.startLocation = state.startLocation
            ? InternalMinimalLocation.fromJS(state.startLocation)
            : state.startLocation;
        state.endShiftInstant = ShiftInstant.fromJS(state.endShiftInstant);
        state.endLocation = state.endLocation
            ? InternalMinimalLocation.fromJS(state.endLocation)
            : state.endLocation;
        state.breaks = Immutable.List(state.breaks).map(item =>
            InternalBreak.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        return this;
    }

    @validate
    setStartLocation(value: ?MinimalLocation): this {
        return super.setStartLocation(value);
    }

    validateStartLocation() {
        const { startLocation } = this;
        if (!startLocation) {
            return plannerNsTranslate(
                'vehicleModelReplaceValidations.startLocationEmptyMsg'
            );
        }
    }

    get startShiftInstant(): ShiftInstant {
        return this._state.get('startShiftInstant');
    }

    get endShiftInstant(): ShiftInstant {
        return this._state.get('endShiftInstant');
    }

    get startLocation(): ?InternalMinimalLocation {
        return this._state.get('startLocation');
    }

    get endLocation(): ?InternalMinimalLocation {
        return this._state.get('endLocation');
    }

    get breaks(): Immutable.List<Break> {
        return this._state.get('breaks');
    }
}

class InternalVisitLocation extends VisitLocation {
    static fromJS(json: VisitLocationModelType): InternalVisitLocation {
        const state: Object = Object.assign({}, json);
        state.slot = InternalVisitSlot.fromJS(state.slot);
        state.contact = state.contact
            ? ContactPoint.fromJS(state.contact)
            : state.contact;
        state.address = StructuredAddress.fromJS(state.address);
        state.geometry = Geometry.fromJS(state.geometry);
        state.geocodingMetadata = GeocodingMetadata.fromJS(
            state.geocodingMetadata
        );
        state.nonAvailableSlots = Immutable.List(
            state.nonAvailableSlots
        ).map(item => InternalVisitSlot.fromJS(item));
        return new this(Immutable.Map(state));
    }

    get slot(): InternalVisitSlot {
        return this._state.get('slot');
    }

    get contact(): ?ContactPoint {
        return this._state.get('contact');
    }

    get address(): StructuredAddress {
        return this._state.get('address');
    }

    get geometry(): Geometry {
        return this._state.get('geometry');
    }

    getFullAddress() {
        return this.address.getFullAddress();
    }

    get geocodingMetadata(): GeocodingMetadata {
        return this._state.get('geocodingMetadata');
    }

    get nonAvailableSlots(): Immutable.List<VisitSlot> {
        return this._state.get('nonAvailableSlots');
    }
}

class InternalTimeDistancePair extends TimeDistancePair {
    static fromJS(json: TimeDistancePairModelType): InternalTimeDistancePair {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalVisitVolume extends VisitVolume {
    static fromJS(json: VisitVolumeModelType): InternalVisitVolume {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalConstraintViolation extends ConstraintViolation {
    static fromJS(
        json: ConstraintViolationModelType
    ): InternalConstraintViolation {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalOrderDetail extends OrderDetail {
    static fromJS(json: OrderDetailModelType): InternalOrderDetail {
        const state: Object = Object.assign({}, json);
        state.lineItems = state.lineItems
            ? Immutable.List(state.lineItems).map(item => LineItem.fromJS(item))
            : state.lineItems;
        return new this(Immutable.Map(state));
    }

    get lineItems(): ?Immutable.List<LineItem> {
        return this._state.get('lineItems');
    }
}

class InternalUserAlias extends UserAlias {
    static fromJS(json: UserAliasModelType): InternalUserAlias {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalVehicleModelId extends VehicleModelId {
    static fromJS(json: VehicleModelIdModelType): InternalVehicleModelId {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalMinimalLocation extends MinimalLocation {
    static fromJS(json: MinimalLocationModelType): InternalMinimalLocation {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalBreak extends Break {
    static fromJS(json: BreakModelType): InternalBreak {
        const state: Object = Object.assign({}, json);
        state.startShiftInstant = ShiftInstant.fromJS(state.startShiftInstant);
        state.startLocation = state.startLocation
            ? InternalMinimalLocation.fromJS(state.startLocation)
            : state.startLocation;
        state.endShiftInstant = ShiftInstant.fromJS(state.endShiftInstant);
        state.endLocation = state.endLocation
            ? InternalMinimalLocation.fromJS(state.endLocation)
            : state.endLocation;
        return new this(Immutable.Map(state));
    }

    get startShiftInstant(): ShiftInstant {
        return this._state.get('startShiftInstant');
    }

    get endShiftInstant(): ShiftInstant {
        return this._state.get('endShiftInstant');
    }

    get startLocation(): ?InternalMinimalLocation {
        return this._state.get('startLocation');
    }

    get endLocation(): ?InternalMinimalLocation {
        return this._state.get('endLocation');
    }

    get breakType(): BreakTypeEnum {
        return this._state.get('breakType');
    }
}

class InternalVisitSlot extends VisitSlot {
    static fromJS(json: VisitSlotModelType): InternalVisitSlot {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalResources extends Resources {
    static fromJS(json: ResourcesModelType): InternalResources {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): ResourcesModelType {
        return this.getState().toJS();
    }
}

class InternalPlanEditsSummary extends PlanEditsSummary {
    static fromJS(json: PlanEditsSummaryModelType): InternalPlanEditsSummary {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    hasEdits(): boolean {
        const { editCount, vehiclesChanged, vehiclesAdded } = this;
        if (editCount !== 0 || vehiclesChanged !== 0 || vehiclesAdded !== 0) {
            return true;
        }

        return false;
    }
}

class InternalMetric extends Metric {
    static fromJS(json: MetricModelType): InternalMetric {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalCustomFieldMetric extends CustomFieldMetric {
    static fromJS(json: CustomFieldMetricModelType): InternalCustomFieldMetric {
        const state: Object = Object.assign({}, json);
        state.metric = InternalMetric.fromJS(state.metric);
        return new this(Immutable.Map(state));
    }

    get metric(): InternalMetric {
        return this._state.get('metric');
    }
}

export {
    InternalPlan as Plan,
    InternalPlanConfig as PlanConfig,
    InternalVehicleGroup as VehicleGroup,
    InternalProgress as Progress,
    InternalVehicleTour as VehicleTour,
    InternalAppFieldItem as AppFieldItem,
    InternalAppFields as AppFields,
    InternalPlanSummary as PlanSummary,
    InternalUnassignedTask as UnassignedTask,
    InternalVehicle as Vehicle,
    InternalTourVisit as TourVisit,
    InternalUserTourSummary as UserTourSummary,
    InternalTourId as TourId,
    InternalUserVehicle as UserVehicle,
    InternalAssignedUser as AssignedUser,
    InternalResourceUtilization as ResourceUtilization,
    InternalVehicleId as VehicleId,
    InternalVehicleModel as VehicleModel,
    InternalOperationShift as OperationShift,
    InternalVisitLocation as VisitLocation,
    InternalTimeDistancePair as TimeDistancePair,
    InternalVisitVolume as VisitVolume,
    InternalConstraintViolation as ConstraintViolation,
    InternalOrderDetail as OrderDetail,
    InternalUserAlias as UserAlias,
    InternalVehicleModelId as VehicleModelId,
    InternalMinimalLocation as MinimalLocation,
    InternalBreak as Break,
    InternalVisitSlot as VisitSlot,
    InternalResources as Resources,
    InternalPlanEditsSummary as PlanEditsSummary,
    InternalCustomFieldMetric as CustomFieldMetric,
    InternalMetric as Metric
};
